<template>
    <section class="image-uploader-container">
        <input
            type="file"
            ref="fileInput"
            class="file-input"
            :accept="allowedTypes.join(',')"
            @change="handleFileSelect"
        />
        <div
            class="upload-area"
            :class="{
                'upload-area-dragging': isDragging,
                'upload-area-error': errorMessage,
            }"
            @click="triggerFileInput"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
        >
            <!-- Empty input area -->
            <template v-if="!showPreview && !loading">
                <StfIcon icon="upload"/>
                <div class="select-text">Select an image</div>
                <div class="drag-text">Or drag an image here to upload</div>
            </template>
            <!-- Preview input area -->
            <template v-else-if="showPreview">
                <div class="preview-container">
                    <img :src="previewUrl" alt="Preview" class="image-preview" />
                    <div class="remove-button" @click.stop="removeFile">
                        <StfIcon icon="circleXmark" size="small" />
                    </div>
                    <div v-if="previewName" class="file-name">{{ previewName }}</div>
                </div>
            </template>
            <!-- Loading input area -->
            <template v-else-if="loading">
                <div class="upload-progress">
                    <div class="progress-text">Uploading...</div>
                    <StfProgressBar/>
                </div>
            </template>
        </div>

        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
    </section>
</template>

<script setup lang="ts">
import {computed, ref} from 'vue';
import StfProgressBar from '@/ui/progress-bar/StfProgressBar.vue';

interface UploadProps {
    maxSizeMB?: number;
    allowedTypes?: string[];
    loading: boolean;
    preview?: string | {url: string; name: string} | null;
    error?: string | null;
}

const props = withDefaults(defineProps<UploadProps>(), {
    maxSizeMB: 5,
    allowedTypes: () => ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    preview: null,
});

const fileInput = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);
const uploadError = ref<string | null>(null);

const showPreview = computed(() => {
    return props.preview !== null && !props.loading;
});

const previewUrl = computed(() => {
    if (props.preview === null) {
        return '';
    }

    if (typeof props.preview === 'object') {
        return props.preview.url;
    }

    return props.preview;
});

const previewName = computed(() => {
    if (typeof props.preview === 'object') {
        return props.preview?.name;
    }

    return null;
});

const errorMessage = computed(() => {
    if (props.error) {
        return props.error;
    }

    if (uploadError.value) {
        return uploadError.value;
    }

    return null;
});

const emit = defineEmits<{
    (e: 'file-selected', file: File): void;
    (e: 'file-removed'): void;
}>();

// Methods
const triggerFileInput = () => {
    if (!props.loading && fileInput.value) {
        fileInput.value.click();
    }
};

const validateFile = (file: File): string | null => {
    // Check file type
    if (!props.allowedTypes.map(type => type.replace('.', 'image/')).includes(file.type)) {
        return `File type not supported. Please upload ${props.allowedTypes.join(', ')}`;
    }

    // Check file size
    const maxSizeBytes = props.maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
        return `File size exceeds ${props.maxSizeMB}MB limit`;
    }

    return null;
};

const handleFileSelect = (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
        processFile(input.files[0]);
    }
};

const handleDragOver = () => {
    isDragging.value = true;
};

const handleDragLeave = () => {
    isDragging.value = false;
};

const handleDrop = (event: DragEvent) => {
    isDragging.value = false;
    if (event.dataTransfer?.files.length) {
        processFile(event.dataTransfer.files[0]);
    }
};

const processFile = (file: File) => {
    uploadError.value = null;

    // Validate file
    const error = validateFile(file);
    if (error) {
        uploadError.value = error;
        return;
    }

    emit('file-selected', file);
};

const removeFile = (event: Event) => {
    event.stopPropagation();
    uploadError.value = null;
    if (fileInput.value) {
        fileInput.value.value = '';
    }

    emit('file-removed');
};

</script>

<style lang="scss">
@use "bulma/sass/utilities/mixins";
@use '@/assets/sass/_variables' as vars;

.image-uploader-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.file-input {
  display: none;
}

.upload-area {
  width: 100%;
  height: 140px;
  border-radius: 10px;
  border: 1px dashed #2D76F6;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  background-color: #deeaff;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;

  &:hover {
    background-color: #d0e0ff;
  }

  &.upload-area-dragging {
    background-color: #c0d6ff;
    border-color: #1a62e0;
    transform: scale(1.02);
  }

  &.upload-area-error {
    border-color: #ff4d4f;
    background-color: #fff2f0;
  }
}

.select-text {
  user-select: text;
  color: #2D76F6;
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-14;
  font-weight: vars.$font-weight-bold;
  margin: 8px 0;
}

.drag-text {
  color: vars.$color-gray-600;
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-14;
  font-weight: vars.$font-weight-medium;
}
.preview-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 10px;
}

.image-preview {
  max-width: 90%;
  max-height: 70%;
  object-fit: contain;
  margin-bottom: 8px;
}

.remove-button {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
}

.file-name {
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-12;
  color: #2D76F6;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-progress {
  width: 80%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.progress-text {
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-12;
  color: vars.$color-gray-600;
  margin: 8px 0;
}

.error-message {
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-12;
  color: vars.$color-error;
  margin-top: 8px;
}
</style>
